import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Clock } from "lucide-react";
import { Separator } from "@/components/ui/separator";

function EventItem({ time, title, children }) {
  return (
    <div className="pb-5 last:pb-0">
      <div className="sm:flex sm:gap-6">
        <div className="sm:w-32 sm:shrink-0 sm:pt-0.5">
          <p className="text-xs sm:text-base font-sans font-medium text-bronze mb-1 sm:mb-0">{time}</p>
        </div>
        <div className="flex-1">
          <h4 className="text-[0.95rem] sm:text-lg font-sans font-semibold text-charcoal mb-0.5">{title}</h4>
          {children && (
            <p className="text-sm sm:text-[0.95rem] font-sans text-charcoal/60 leading-relaxed">{children}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function DaySection({ label, title, children }) {
  return (
    <div className="mb-10 sm:mb-16">
      <div className="mb-6 sm:mb-8">
        <p className="font-sans text-xs sm:text-sm font-semibold uppercase tracking-widest text-bronze mb-1 sm:mb-2">{label}</p>
        <h2 className="font-serif text-xl sm:text-3xl font-medium text-charcoal">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function SaturdayGroup({ title, children }) {
  return (
    <div className="mb-7 sm:mb-10">
      <div className="flex items-center gap-3 mb-4 sm:mb-5">
        <div className="h-px flex-1 bg-sand" />
        <h3 className="font-serif text-base sm:text-xl font-medium text-forest whitespace-nowrap">{title}</h3>
        <div className="h-px flex-1 bg-sand" />
      </div>
      {children}
    </div>
  );
}

export default function ActivitiesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-paper py-10 sm:py-16 lg:py-20">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Link
          to="/"
          data-testid="activities-back-link"
          className="inline-flex items-center text-sm font-sans text-charcoal/60 hover:text-charcoal mb-8 transition-colors"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Home
        </Link>

        {/* Page Title */}
        <div className="mb-8 sm:mb-16">
          <h1
            data-testid="activities-heading"
            className="font-serif text-3xl sm:text-4xl font-semibold text-charcoal mb-4"
          >
            150 Years of Family, History & Story
          </h1>
          <p className="text-[0.95rem] sm:text-lg font-sans text-charcoal/70 leading-relaxed">
            A weekend to remember, reconnect, and celebrate 150 years of
            McCartney family history in New Zealand.
          </p>
          <div className="flex items-center gap-2 mt-4 text-sm font-sans text-charcoal/50">
            <Clock size={14} />
            <span>Times are approximate and may be adjusted closer to the event.</span>
          </div>
        </div>

        {/* FRIDAY */}
        <DaySection label="Friday 22 January 2027" title="Arrival, Welcome & Memory Lane">
          <div className="bg-parchment border border-sand rounded-md p-4 sm:p-8">
            <EventItem time="From 5:00pm" title="Arrival & Registration">
              Arrive at Croydon Lodge, register, and collect your welcome pack.
            </EventItem>
            <EventItem time="From 6:00pm" title="Light Food & Bar Available">
              Light food will be available, with the bar open for drinks.
            </EventItem>
            <EventItem time="6:15pm" title="Formal Welcome: A Walk Down Memory Lane">
              Officially welcome everyone, open the reunion weekend, and take
              a walk down memory lane.
            </EventItem>
            <EventItem time="9:00pm" title="Formal Close">
              The formal part of the evening closes at 9:00pm, with time to
              continue catching up informally.
            </EventItem>
          </div>
          <div className="flex items-center gap-2 mt-4 text-sm font-sans text-charcoal/45">
            <MapPin size={14} className="shrink-0" />
            <span>Croydon Lodge, Gore, Southland</span>
          </div>
        </DaySection>

        <Separator className="mb-10 sm:mb-16 bg-sand" />

        {/* SATURDAY */}
        <DaySection label="Saturday 23 January 2027" title="A Day of Family, History & Celebration">
          {/* Morning */}
          <SaturdayGroup title="Morning: Remembering & Exploring">
            <div className="bg-parchment border border-sand rounded-md p-4 sm:p-8">
              <EventItem time="9:15am" title="Gather at Croydon Lodge">
                Gather at Croydon Lodge before heading off together for the
                first part of the day.
              </EventItem>
              <EventItem time="10:00am" title="Remembrance Visit: Tommy & Agnes McCartney">
                Travel together to Riversdale Cemetery. Those who wish to
                attend will have the opportunity to pay respects at the resting
                place of Tommy and Agnes McCartney.
              </EventItem>
              <EventItem time="11:00am" title="Magical Mystery Family History Tour">
                For those joining from this point, meet at Riversdale. Continue
                the local family history tour, including a few special stops,
                rare access arranged by invitation, and stories along the way.
              </EventItem>
            </div>
          </SaturdayGroup>

          {/* Midday */}
          <SaturdayGroup title="Midday: Local Treats & Family Catch-Up">
            <div className="bg-parchment border border-sand rounded-md p-4 sm:p-8">
              <EventItem time="12:30pm" title="Midday Stop at Miss Cocoa Coffee">
                A chance to enjoy delicious local treats and warm drinks, with
                a fun playground for the children and the historic Mandeville
                Air Museum on site for anyone keen to explore.
              </EventItem>
            </div>
          </SaturdayGroup>

          {/* Afternoon */}
          <SaturdayGroup title="Afternoon: One for the History Books">
            <div className="bg-parchment border border-sand rounded-md p-4 sm:p-8">
              <EventItem time="3:00pm" title="Formal Group Photo">
                Please join at Croydon Lodge for the formal reunion group photo.
                This will be one of the moments to look back on together for
                years to come.
              </EventItem>
            </div>
          </SaturdayGroup>

          {/* Evening */}
          <SaturdayGroup title="Evening: 150 Years in New Zealand">
            <div className="bg-parchment border border-sand rounded-md p-4 sm:p-8">
              <EventItem time="6:30pm" title="Dinner, Song & Story">
                An evening of food, family, stories, songs, and laughter,
                including the cutting of the 150-year anniversary cake. Each
                family branch will be invited to share a little — stories,
                memories, funny moments, and pieces of family history carried
                through the generations.
              </EventItem>
              <EventItem time="9:00pm" title="Time to Farewell">
                The formal evening programme closes at 9:00pm.
              </EventItem>
            </div>
          </SaturdayGroup>

          {/* Still Catching Up */}
          <SaturdayGroup title="Still Catching Up?">
            <p className="text-sm sm:text-[0.95rem] font-sans text-charcoal/60 leading-relaxed text-center px-4">
              Informal catching up continues after the formal programme closes.
              There is no rush — enjoy the evening.
            </p>
          </SaturdayGroup>
        </DaySection>

        <Separator className="mb-10 sm:mb-16 bg-sand" />

        {/* SUNDAY */}
        <DaySection label="Sunday 24 January 2027" title="Farewells, Family Time & Further Exploring">
          <div className="bg-parchment border border-sand rounded-md p-4 sm:p-8">
            <p className="text-[0.95rem] sm:text-base font-sans text-charcoal/70 leading-relaxed">
              There is no formal programme on Sunday. For those staying on,
              this is a chance to keep conversations going, say farewell,
              revisit family history locations, or explore more of the
              Southland area at your own pace.
            </p>
          </div>
        </DaySection>

        {/* Note */}
        <div className="text-center mt-4">
          <p className="text-sm font-sans text-charcoal/40">
            More details including transport information will be shared closer to the event.
          </p>
        </div>
      </div>
    </div>
  );
}
